'use client';

import React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../utils/cn';
import type { LoadingOverlayProps } from './loading-overlay.types';
import styles from './loading-overlay.module.css';
import Text from '../../atoms/text/text';

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
    message = 'Loading...',
    testID,
    className,
    showIf,
}) => {
    if (!showIf || typeof document === 'undefined') {
        return null;
    }

    const overlayUI = (
        <div className={styles.root} data-testid={testID}>
            <div className={cn(styles.content, className)}>
                <div className={styles.logoWrap} data-testid={`${testID}-indicator`}>
                    <svg
                        className={styles.logo}
                        width="54"
                        height="54"
                        viewBox="0 0 54 54"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                    >
                        <path
                            className={styles.bg}
                            d="M0 26.9578C0 19.8081 2.83322 12.9513 7.87638 7.89574C12.9195 2.84018 19.7595 0 26.8916 0C34.0237 0 40.8637 2.84018 45.9069 7.89574C50.9501 12.9513 53.7833 19.8081 53.7833 26.9578C53.7833 41.7794 43.2397 53.7833 26.9119 53.7833C10.5841 53.7833 0.0304421 41.7794 0.0304421 26.9578"
                            fill="#21409A"
                        />
                        <path
                            className={styles.yellow}
                            d="M33.3357 26.9588L39.2417 21.0586C39.7922 20.5069 40.2288 19.8519 40.5268 19.1311C40.8247 18.4102 40.978 17.6375 40.978 16.8573C40.978 16.077 40.8247 15.3043 40.5268 14.5835C40.2288 13.8626 39.7922 13.2076 39.2417 12.6559C38.1195 11.5464 36.6066 10.9243 35.0304 10.9243C33.4542 10.9243 31.9413 11.5464 30.8191 12.6559L24.4057 19.0749C25.1287 18.3846 26.0923 18.005 27.0907 18.0172C28.0891 18.0294 29.0432 18.4325 29.7493 19.1402C30.4553 19.848 30.8573 20.8044 30.8695 21.8053C30.8817 22.8062 30.503 23.7722 29.8144 24.497L27.3485 26.9588L29.8144 29.4206C30.1832 29.7741 30.4777 30.1979 30.6807 30.6672C30.8836 31.1365 30.9909 31.6417 30.9961 32.1532C31.0014 32.6646 30.9045 33.172 30.7112 33.6453C30.518 34.1187 30.2322 34.5485 29.8707 34.9095C29.5093 35.2705 29.0794 35.5554 28.6065 35.7473C28.1336 35.9393 27.6271 36.0345 27.1169 36.0273C26.6067 36.0202 26.1032 35.9108 25.6358 35.7056C25.1684 35.5004 24.7467 35.2035 24.3955 34.8325L30.2609 40.692C30.8113 41.2464 31.4652 41.6867 32.1853 41.9877C32.9055 42.2888 33.6777 42.4447 34.4579 42.4466C35.2381 42.4485 36.0111 42.2963 36.7327 41.9988C37.4542 41.7012 38.1103 41.2641 38.6633 40.7123C39.2163 40.1606 39.6556 39.5051 39.9559 38.7832C40.2562 38.0613 40.4117 37.2872 40.4136 36.505C40.4155 35.7229 40.2637 34.948 39.9669 34.2247C39.67 33.5013 39.234 32.8437 38.6836 32.2893L33.3357 26.9588Z"
                            fill="#FFCC32"
                        />
                        <path
                            className={styles.cyan}
                            d="M16.5208 26.9694L24.3549 19.1567V16.8679C24.3549 15.2842 23.7273 13.7653 22.6102 12.6455C21.4931 11.5256 19.978 10.8965 18.3982 10.8965C16.8183 10.8965 15.3032 11.5256 14.1861 12.6455C13.069 13.7653 12.4414 15.2842 12.4414 16.8679V36.4402C12.4414 38.0239 13.069 39.5428 14.1861 40.6626C15.3032 41.7825 16.8183 42.4116 18.3982 42.4116C19.978 42.4116 21.4931 41.7825 22.6102 40.6626C23.7273 39.5428 24.3549 38.0239 24.3549 36.4402V34.7922L16.5208 26.9694Z"
                            fill="#20C4F4"
                        />
                        <path
                            className={styles.white}
                            d="M27.3482 26.957L29.8141 24.4952C30.5028 23.7703 30.8814 22.8044 30.8692 21.8035C30.8571 20.8026 30.455 19.8462 29.749 19.1384C29.043 18.4306 28.0889 18.0276 27.0904 18.0154C26.092 18.0032 25.1284 18.3828 24.4054 19.0731L16.5713 26.8858L24.4054 34.7086C24.7566 35.0796 25.1783 35.3765 25.6457 35.5817C26.113 35.7869 26.6166 35.8963 27.1268 35.9034C27.637 35.9106 28.1434 35.8154 28.6164 35.6235C29.0893 35.4315 29.5191 35.1466 29.8806 34.7856C30.2421 34.4246 30.5278 33.9948 30.7211 33.5214C30.9144 33.0481 31.0112 32.5407 31.006 32.0293C31.0008 31.5178 30.8935 31.0126 30.6906 30.5433C30.4876 30.074 30.1931 29.6502 29.8243 29.2967L27.3482 26.957Z"
                            fill="white"
                        />
                    </svg>
                </div>

                <Text className={styles.text} data-testid={`${testID}-message`}>
                    {message}
                </Text>
            </div>
        </div>
    );

    return createPortal(overlayUI, document.body);
};
